import React from "react";
import { IntegrationRendererInterface } from "../../../../packages/excalidraw/element/types";
import { RenderNotion } from "./RenderNotion";
import { RenderMindmapNode } from "./RenderMindmapNode";

interface BoundaryProps {
  /** Shown in the neutral fallback pill when the real renderer throws. */
  label?: string | null;
  children: React.ReactNode;
}

/**
 * Per-element error boundary. Integration elements carry data straight from the
 * Notion API and from scenes written by older versions, so a single malformed
 * element must degrade to a neutral pill instead of unmounting the whole editor
 * tree (S2).
 */
class IntegrationErrorBoundary extends React.Component<
  BoundaryProps,
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          data-testid="integration-render-error"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            padding: "0 12px",
            boxSizing: "border-box",
            borderRadius: 999,
            border: "1px solid var(--default-border-color, #ccc)",
            color: "var(--text-primary-color, #888)",
            fontSize: 13,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {this.props.label ?? ""}
        </div>
      );
    }
    return <>{this.props.children}</>;
  }
}

const RenderIntegrationInner: React.FC<IntegrationRendererInterface> = ({
  element,
  parentStyle,
  updateScene,
  viewModeEnabled,
}) => {
  if (element.type === "notion") {
    return (
      <IntegrationErrorBoundary label={element.title}>
        <RenderNotion
          element={element}
          parentStyle={parentStyle}
          updateScene={updateScene}
        />
      </IntegrationErrorBoundary>
    );
  }
  if (element.type === "mindmapNode") {
    return (
      <IntegrationErrorBoundary label={element.label}>
        <RenderMindmapNode
          element={element}
          parentStyle={parentStyle}
          updateScene={updateScene}
          viewModeEnabled={viewModeEnabled}
        />
      </IntegrationErrorBoundary>
    );
  }
  return <></>;
};

// Rendered once per on-screen integration element on every scroll/zoom frame;
// App.tsx hands it stable props so memoizing here cuts the whole subtree (S5).
export const RenderIntegration = React.memo(RenderIntegrationInner);
